'use client';

import * as React from 'react';
import config from '@lib/config';
import useSystemSkills from '@lib/shared/useSystemSkills';
import { Autocomplete } from '@/ui';
import styles from './SkillSelect.module.css';

const DEFAULT_SKILL_LOGO: string = config.defaultSkillLogo;

export interface SystemSkill {
  id: number | string;
  name: string;
  logo?: string | null;
}

export interface CustomSkill {
  id: string;
  name: string;
  isCustom: true;
}

export interface SkillSelectProps {
  /** Skill ids already attached to the record, hidden from the option list. */
  exclude?: Array<number | string>;
  /** Receives `{ id }` for an existing skill, or a `CustomSkill` for a free-text entry. */
  onChange: (skill: { id: number | string } | CustomSkill) => void;
  placeholder?: string;
  label?: string;
}

function SkillOption({ skill }: { skill: SystemSkill }) {
  return (
    <span className={styles.option}>
      <img
        className={styles.logo}
        src={skill.logo || DEFAULT_SKILL_LOGO}
        alt=""
        title={skill.name}
      />
      <span className={styles.name}>{skill.name}</span>
    </span>
  );
}

export default function SkillSelect({
  exclude = [],
  onChange,
  placeholder = 'Type the name of the skill or add a custom one...',
  label,
}: SkillSelectProps) {
  const { skills, isLoading } = useSystemSkills() as {
    skills: SystemSkill[];
    isLoading: boolean;
  };

  const options = React.useMemo(
    () => skills.filter((skill) => !exclude.includes(skill.id)),
    [skills, exclude]
  );

  return (
    <Autocomplete<SystemSkill>
      items={options}
      label={label}
      placeholder={placeholder}
      size="lg"
      loading={isLoading}
      loadingText="Loading..."
      emptyText="No results"
      getOptionLabel={(skill) => skill.name}
      getOptionKey={(skill) => skill.id}
      renderOption={(skill) => <SkillOption skill={skill} />}
      onSelect={(skill) => onChange({ id: skill.id })}
      allowCustomValue
      onCreate={(value) =>
        onChange({ id: `custom-${Date.now()}`, name: value, isCustom: true })
      }
    />
  );
}
